import React, { useRef, useEffect } from 'react';
import { useFullScreenHandle } from 'react-full-screen'
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { StyleFullScreen, MapWrapper } from '../style/map_components';
import '../style/legend_map.css';
import { CHRONOLOGY_LEVEL_COLOR, CHRONOLOGY_LEVEL_LABEL } from '../const/color';
import MapFullscreenIcon from '../item/map_fullscreen_icon'
import MapSelectLayersIcon from '../item/map_select_layers_icon'
import MapChronologyLegendIcon from '../item/map_chronology_legend_icon';
import bbox from '@turf/bbox';
import { setChronologyLevelColor } from '../func/color';

const sourceSetting = {
    "osm": {
        "type": "raster",
        "tiles": ["https://cyberjapandata.gsi.go.jp/xyz/seamlessphoto/{z}/{x}/{y}.jpg"],
        "tileSize": 256,
        "attribution": "MapLibre | 地図の出典：<a href='https://maps.gsi.go.jp/development/ichiran.html' target='_blank'>国土地理院</a>"
    }
};

// 背景地図の設定
const baseLayer = [
    {
        "id": "osm-tiles",
        "type": "raster",
        "source": "osm",
        "minzoom": 0,
        "maxzoom": 24
    }
];

const MapComponent = (props) => {
    const mapContainer = useRef(null);
    const map = useRef(null);

    // --- ベース地図 URL 定数 ---
    const SATELLITE_URL = 'https://cyberjapandata.gsi.go.jp/xyz/seamlessphoto/{z}/{x}/{y}.jpg';
    const PALE_URL = 'https://cyberjapandata.gsi.go.jp/xyz/pale/{z}/{x}/{y}.png';

    // ベース地図切替ハンドラ（MapSelectLayersIcon の onChangeBaseMap から呼ぶ）
    const handleBaseMapChange = (value) => {
        if (!map.current || !map.current.isStyleLoaded()) return;
        const tilesUrl = value === 'pale' ? PALE_URL : SATELLITE_URL;

        try {
            if (map.current.getLayer && map.current.getLayer('osm-tiles')) {
                map.current.removeLayer('osm-tiles');
            }
            if (map.current.getSource && map.current.getSource('osm')) {
                map.current.removeSource('osm');
            }

            const paint = value === 'pale' ? { 'raster-saturation': -1 } : {};

            map.current.addSource('osm', {
                type: 'raster',
                tiles: [tilesUrl],
                tileSize: 256,
                attribution: "MapLibre | 地図の出典：<a href='https://maps.gsi.go.jp/development/ichiran.html' target='_blank'>国土地理院</a>"
            });

            const styleLayers = map.current.getStyle && map.current.getStyle().layers;
            const beforeId = Array.isArray(styleLayers) && styleLayers.length ? styleLayers[0].id : undefined;

            const layerDef = {
                id: 'osm-tiles',
                type: 'raster',
                source: 'osm',
                minzoom: 0,
                maxzoom: 24
            };
            if (Object.keys(paint).length) {
                layerDef.paint = paint;
            }
            map.current.addLayer(layerDef, beforeId);
        } catch (err) {
            console.error('handleBaseMapChange error:', err);
        }
    };

    // 監視用Ref
    const chronologyDataRef = useRef(props.chronologyList);
    const selectCitysRef = useRef(props.selectCitys);

    const mapStyle = {
        height: '100%',
        width: '100%',
        margin: '0 auto'
    }

    // 全画面表示切り替え
    const fullscreenHandle = useFullScreenHandle()

    const layersSetting = [...baseLayer]

    let hoveredId = null

    const checkData = (data) => {
        if (!data || data === undefined || data === null || data.length < 1) {
            return false
        }
        return true
    }

    // レイヤの色分け
    const setLayerStyle = () => {
        // フィルター済みのクロノロジーデータ
        if (map.current && map.current.isStyleLoaded()) {
            // クロノロジーデータ表示
            addChronologyLayer();
            // クロノロジーデータを GeoJSON に変換
            const geo = buildChronologyGeoJSON(chronologyDataRef.current);
            map.current.getSource('chronology').setData(geo);
            // クロノロジーの色分け
            setChronologyStyle();

            // ズーム・センター調整
            if (checkData(chronologyDataRef.current)) {
                setMapCenterZoom(geo);
            }
        }
    }

    // クロノロジースタイル変更
    const setChronologyStyle = () => {
        // データがなければ透明化して終了
        if (!checkData(chronologyDataRef.current)) {
            map.current.setPaintProperty('chronology', 'circle-color', 'rgba(0,0,0,0)');
            map.current.setPaintProperty('chronology', 'circle-stroke-color', 'rgba(0,0,0,0)');
            return;
        }

        // 塗り色を設定
        const selectedData = chronologyDataRef.current
        const matchExpression_fill = ['match', ['get', 'code']];
        const matchExpression_stroke = ['match', ['get', 'code']];

        for (const item of selectedData) {
            matchExpression_fill.push(
                item.code,
                setChronologyLevelColor(item.TXT_MAIN_DAMAGE_NAME || item.txt_main_damage_name));

            matchExpression_stroke.push(item.code, '#ffffff');
        }
        matchExpression_fill.push('rgba(0,0,0,0)');
        matchExpression_stroke.push('rgba(0,0,0,0)');

        // レイヤに適用
        map.current.setPaintProperty('chronology', 'circle-color', matchExpression_fill);
        map.current.setPaintProperty('chronology', 'circle-stroke-color', matchExpression_stroke);
    };

    // 地図のセンターとズーム調整
    const setMapCenterZoom = (geo) => {
        if (!map.current) return;
        if (geo.features.length === 0) return;

        // Turf.js で bbox を取得
        const bounds = bbox(geo); // [west, south, east, north]

        map.current.fitBounds(bounds, {
            padding: 30,
            maxZoom: 13,   // 拡大制限
            duration: 800  // アニメーション時間
        });
    }

    // --- クロノロジー用 GeoJSON 作成 ---
    const buildChronologyGeoJSON = (list) => {
        const fc = { type: "FeatureCollection", features: [] };
        if (!Array.isArray(list) || list.length === 0) return fc;

        const toNumber = (v) => {
            const n = Number(v);
            return Number.isFinite(n) ? n : 0;
        };

        const features = list.map(item => {
            const lat = item.DC_LAT_NUM ?? item.dc_lat_num ?? item.lat ?? null;
            const lon = item.DC_LNG_NUM ?? item.dc_lng_num ?? item.lng ?? item.lon ?? null;
            if (lat == null || lon == null) return null;
            const coordsLon = Number(lon);
            const coordsLat = Number(lat);
            if (!Number.isFinite(coordsLon) || !Number.isFinite(coordsLat)) return null;

            return {
                type: "Feature",
                geometry: {
                    type: "Point",
                    coordinates: [coordsLon, coordsLat]
                },
                properties: {
                    code: item.code,
                    city_code: item.city_code,
                    city_name: item.city_name,
                    area_code: item.area_code,
                    area_name: item.area_name,
                    msh_code: item.msh_code,
                    tsm_recept_date_sum: item.TSM_RECEPT_DATE_SUM || item.tsm_recept_date_sum,
                    txt_act_stts_name: item.TXT_ACT_STTS_NAME || item.txt_act_stts_name ,
                    int_collapse_num: toNumber(item.INT_COLLAPSE_NUM || item.int_collapse_num),
                    int_landslide_num: toNumber(item.INT_LANDSLIDE_NUM || item.int_landslide_num),
                    int_flood_num: toNumber(item.INT_FLOOD_NUM || item.int_flood_num),
                    int_fallen_tree_num: toNumber(item.INT_FALLEN_TREE_NUM || item.int_fallen_tree_num),
                    int_power_outage_num: toNumber(item.INT_POWER_OUTAGE_NUM || item.int_power_outage_num),
                    int_water_outage_num: toNumber(item.INT_WATER_OUTAGE_NUM || item.int_water_outage_num),
                    int_other_num: toNumber(item.INT_OTHER_NUM || item.int_other_num),
                    int_human_num: toNumber(item.INT_HUMAN_NUM || item.int_human_num),
                    int_regulation_num: toNumber(item.INT_REGULATION_NUM || item.int_regulation_num),
                    int_river_burst_num: toNumber(item.INT_RIVER_BURST_NUM || item.int_river_burst_num),
                    int_all_num: toNumber(item.INT_ALL_NUM || item.int_all_num),
                    txt_main_damage_name: item.TXT_MAIN_DAMAGE_NAME || item.txt_main_damage_name,
                    int_main_damage_num: toNumber(item.INT_MAIN_DAMAGE_NUM || item.int_main_damage_num),
                    int_finished_num: toNumber(item.INT_FINISHED_NUM || item.int_finished_num),
                    int_ongoing_num: toNumber(item.INT_ONGOING_NUM || item.int_ongoing_num)
                }
            };
        }).filter(Boolean);

        fc.features = features;
        return fc;
    };

    // --- Map にクロノロジー用ソース & レイヤを追加 ---
    const addChronologyLayer = () => {
        if (!map.current || !map.current.isStyleLoaded()) return;
        if (map.current.getSource('chronology')) return;

        map.current.addSource('chronology', {
            type: 'geojson',
            data: { type: "FeatureCollection", features: [] }
        });

        map.current.addLayer({
            id: 'chronology',
            type: 'circle',
            source: 'chronology',
            paint: {
                'circle-radius': [
                    'interpolate',
                    ['linear'],
                    ['zoom'],
                    5, 6,
                    10, 8,
                    12, 10,
                    15, 12,
                    18, 14
                ],
                'circle-stroke-width': 1.0,
                'circle-stroke-color': '#ffffff',
                'circle-color': '#0000FF',
                'circle-opacity': 1
            },
            layout: {
                visibility: 'visible'
            }
        });

        // クリック
        map.current.on('click', 'chronology', handleClick);
        // ホバーでツールチップ（mousemove）とカーソル・離脱処理
        map.current.on('mousemove', 'chronology', handleMouseMove);
        map.current.on('mouseleave', 'chronology', handleMouseLeave);
        // カーソル切替
        map.current.on('mouseenter', 'chronology', () => {
            map.current.getCanvas().style.cursor = 'pointer';
        });
        map.current.on('mouseleave', 'chronology', () => {
            map.current.getCanvas().style.cursor = '';
        });
    };

    // 被害名から色を決めるヘルパー
    const getDamageColor = (name) => {
        const map = {
            [CHRONOLOGY_LEVEL_LABEL.LEVER_1]: CHRONOLOGY_LEVEL_COLOR.LEVER_1,
            [CHRONOLOGY_LEVEL_LABEL.LEVER_2]: CHRONOLOGY_LEVEL_COLOR.LEVER_2,
            [CHRONOLOGY_LEVEL_LABEL.LEVER_3]: CHRONOLOGY_LEVEL_COLOR.LEVER_3,
            [CHRONOLOGY_LEVEL_LABEL.LEVER_4]: CHRONOLOGY_LEVEL_COLOR.LEVER_4,
            [CHRONOLOGY_LEVEL_LABEL.LEVER_5]: CHRONOLOGY_LEVEL_COLOR.LEVER_5,
            [CHRONOLOGY_LEVEL_LABEL.LEVER_6]: CHRONOLOGY_LEVEL_COLOR.LEVER_6,
            [CHRONOLOGY_LEVEL_LABEL.LEVER_7]: CHRONOLOGY_LEVEL_COLOR.LEVER_7,
            [CHRONOLOGY_LEVEL_LABEL.LEVER_8]: CHRONOLOGY_LEVEL_COLOR.LEVER_8,
            [CHRONOLOGY_LEVEL_LABEL.LEVER_9]: CHRONOLOGY_LEVEL_COLOR.LEVER_9
        };
        if (map[name]) return map[name];
        // fallback: string → 色ハッシュ (HSL)
        const hash = Array.from(name).reduce((h, c) => h + c.charCodeAt(0), 0);
        const hue = hash % 360;
        return `hsl(${hue} 70% 50%)`;
    };

    // マウスホバー時の処理
    const handleMouseMove = (e) => {
        const feature = e.features && e.features[0];
        if (!feature) return;
        const props = feature.properties || {};
        const id = props.code ?? props.id ?? feature.id;
        if (id === undefined || id === null) return;

        // すでにホバー中なら無視
        if (hoveredId === id) return;

        const chronologyData = chronologyDataRef.current;
        if (!Array.isArray(chronologyData) || chronologyData.length === 0) return;

        const data = chronologyData.find(item => item.code === Number(id) || item.code === id);

        // 前のポップアップを削除
        if (map.current.__hoverPopup) {
            map.current.__hoverPopup.remove();
            map.current.__hoverPopup = null;
        }

        if (!data) {
            map.current.getCanvas().style.cursor = '';
            return;
        } else {
            map.current.getCanvas().style.cursor = 'pointer';
        }
        hoveredId = id;

        const damageName = data.TXT_MAIN_DAMAGE_NAME || data.txt_main_damage_name || '-';
        const dotColor = getDamageColor(damageName);

        const popup = new maplibregl.Popup({
            closeButton: false,
            closeOnClick: false
        })
            .setLngLat(e.lngLat)
            .setHTML(`
                <div style="color:black;max-width:320px;">
                    <div style="font-weight:normal;display:flex;align-items:center;">
                        <div style="margin-right:6px;">主要な被害名：</div>
                        <span style="display:inline-block;width:12px;height:12px;border-radius:50%;background:${dotColor};"></span>
                        <strong style="margin-left:6px;">${damageName}</strong>
                    </div>
                    <div>字名：<strong>${data.area_name || props.area_name || '-'}</strong></div>
                    <div>市町名：<strong>${data.city_name || props.city_name || '-'}</strong></div>
                    <div>更新日時（サマリ）：<strong>${data.TSM_RECEPT_DATE_SUM || data.tsm_recept_date_sum || '-'}</strong></div>
                </div>
            `)
            .addClassName('custom-popup')
            .addTo(map.current);

        map.current.__hoverPopup = popup;
    };

    // レイヤーマウスアウト時の処理
    const handleMouseLeave = () => {
        map.current.getCanvas().style.cursor = '';
        if (map.current.__hoverPopup) {
            map.current.__hoverPopup.remove();
            map.current.__hoverPopup = null;
            hoveredId = null
        }
    };

    // レイヤークリック時の処理
    const handleClick = (e) => {
        // レイヤー単位の click ハンドラ（features を期待）
        if (!e || !e.features || e.features.length === 0) return;

        const feature = e.features[0];
        if (!feature || !feature.properties) return;

        const featureId = feature.properties.msh_code ?? null;
        const label = feature.properties?.txt_main_damage_name ?? null;
        if (typeof props.setSelectMsh === 'function' && featureId != null) {
            props.setSelectMsh([featureId]);
        }
        if (typeof props.setSelectLabelChronology === 'function' && label != null) {
            props.setSelectLabelChronology(label);
        }
    };

    // ロード時の処理
    const handleLoad = () => {
        if (!map.current) return;
        // レイヤ色付け
        setLayerStyle();
    };

    // マップ初期化処理
    useEffect(() => {
        if (map.current) return;
        map.current = new maplibregl.Map({
            container: mapContainer.current,
            center: props.riskMapCenter,
            zoom: props.riskMapZoom,
            style: {
                version: 8,
                sources: sourceSetting,
                layers: layersSetting
            },
            attributionControl: false
        });
        map.current.addControl(new maplibregl.NavigationControl({
            // コンパス表示なし
            showCompass: false,
            showZoom: true
        }), 'top-left');
        map.current.addControl(new maplibregl.AttributionControl({
            // Attributionは折りたたまない
            compact: false
        }), 'bottom-right');
        map.current.on('load', handleLoad);

        return () => {
            if (!map.current) return;
            map.current.off('load', handleLoad);
            // map.current.off('click', (e) => handleClick(e));
            map.current.getCanvas().removeEventListener('mouseleave', handleMouseLeave);
            map.current.remove();
            map.current = null;
        };
    }, []);

    useEffect(() => {
        // 更新用に ref も更新
        chronologyDataRef.current = props.chronologyList;

        if (!map.current || !map.current.isStyleLoaded()) return;

        const geo = buildChronologyGeoJSON(props.chronologyList || []);

        // ソースが存在すればデータをセット、なければ作る
        try {
            if (map.current.getSource && map.current.getSource('chronology')) {
                map.current.getSource('chronology').setData(geo);
            } else {
                map.current.addSource('chronology', {
                    type: 'geojson',
                    data: geo
                });
                // レイヤーが存在しなければ addLayer する（既に追加済みならスキップ）
                if (!map.current.getLayer || !map.current.getLayer('chronology')) {
                    map.current.addLayer({
                        id: 'chronology',
                        type: 'circle',
                        source: 'chronology',
                        paint: {
                            'circle-radius': [
                                'interpolate',
                                ['linear'],
                                ['zoom'],
                                5, 6,
                                10, 8,
                                12, 10,
                                15, 12,
                                18, 14
                            ],
                            'circle-stroke-width': 1.0,
                            'circle-stroke-color': '#ffffff',
                            'circle-color': '#0000FF',
                            'circle-opacity': 1
                        },
                        layout: { visibility: 'visible' }
                    });
                }
            }
        } catch (err) {
            console.error('update chronology source error:', err);
        }

        // 色分けや stroke の更新
        try {
            setChronologyStyle();
        } catch (err) {
            console.error('setChronologyStyle error:', err);
        }

        // 表示用に中心とズームも調整（要素があれば）
        if (geo && geo.features && geo.features.length > 0) {
            setMapCenterZoom(geo);
        }
    }, [props.chronologyList]);

    // 市町村チェックボックス変更時に呼ばれる
    useEffect(() => {
        selectCitysRef.current = props.selectCitys

        // レイヤの色分け、中心・ズーム調整
        setLayerStyle()
    }, [props.selectCitys]);

    // 地域レイヤークリック時に呼ばれる
    useEffect(() => {
        chronologyDataRef.current = props.chronologyList

        if (!map.current) return;
        const interval = setInterval(() => {
            if (map.current?.isStyleLoaded()) {
                setLayerStyle();
                clearInterval(interval);
            }
        }, 100);

        return () => clearInterval(interval);
    }, [props.chronologyList]);

    return (
        <StyleFullScreen
            handle={fullscreenHandle}
            enabled={true}
        >
            <MapWrapper>
                <div
                    ref={mapContainer}
                    className=""
                    style={mapStyle}
                />
                <MapFullscreenIcon
                    fullscreenHandle={fullscreenHandle}
                    top = {'70px'}
                />
                <MapSelectLayersIcon
                    onChangeBaseMap={handleBaseMapChange}
                    pageType="chronology"
                />
                <MapChronologyLegendIcon />
            </MapWrapper>
        </StyleFullScreen>
    );
};
export default MapComponent;